import { DialogComponent } from '@theme/dialog';
import { onAnimationEnd } from '@theme/utilities';

const CORNER_CLASS = 'mailchimp-popup__content--corner';
const EXIT_INTENT_DELAY = 1500;
const SUBSCRIBED_KEY = 'makus:mailchimp-subscribed';
const SUCCESS_CLOSE_DELAY = 4000;

/**
 * @returns {boolean}
 */
export function hasMailchimpSubscribed() {
  try {
    return localStorage.getItem(SUBSCRIBED_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Persist that the visitor submitted the Mailchimp form on this browser.
 */
export function markMailchimpSubscribed() {
  try {
    localStorage.setItem(SUBSCRIBED_KEY, '1');
  } catch {
    // Private mode can throw.
  }

  document.dispatchEvent(new CustomEvent('mailchimp:subscribed'));
}

/**
 * Mailchimp signup dialog. The theme-layout instance (`data-exit-intent`)
 * opens a non-modal card in the bottom-right when the pointer leaves the
 * viewport. Block instances stay a centered modal opened by a CTA.
 *
 * @extends {DialogComponent}
 */
class MailchimpPopup extends DialogComponent {
  /** @type {boolean} */
  #isCorner = false;

  /** @type {number | null} */
  #intentTimer = null;

  /** @type {() => Promise<void>} */
  #parentCloseDialog;

  /** @type {string | null} */
  #submitLabel = null;

  constructor() {
    super();

    // DialogComponent.closeDialog is an instance field, so super.closeDialog() is undefined.
    this.#parentCloseDialog = this.closeDialog;
    this.closeDialog = this.#closeDialog;
  }

  get #isExitIntent() {
    return this.dataset.exitIntent === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    this.#bindFormSubmit();

    if (!this.#isExitIntent) return;
    if (hasMailchimpSubscribed()) return;
    if (window.Shopify?.designMode) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    document.addEventListener('mailchimp:subscribed', this.#onSubscribed);

    this.#intentTimer = window.setTimeout(() => {
      document.documentElement.addEventListener('mouseleave', this.#onDocumentLeave);
    }, EXIT_INTENT_DELAY);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopWatching();
    document.removeEventListener('mailchimp:subscribed', this.#onSubscribed);
    this.removeEventListener('submit', this.#onFormSubmit);
  }

  /**
   * Button CTA: centered modal with backdrop. Exit-intent instances stay a corner card.
   */
  showDialog() {
    if (this.#isExitIntent) {
      this.showCornerDialog();
      return;
    }

    this.#isCorner = false;
    this.refs.dialog.classList.remove(CORNER_CLASS);
    this.refs.dialog.setAttribute('scroll-lock', '');
    super.showDialog();
  }

  /**
   * Exit-intent: non-modal, no backdrop, bottom-right.
   */
  showCornerDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;
    if (hasMailchimpSubscribed()) return;
    if (this.#isBlockModalOpen()) return;

    this.#isCorner = true;
    this.#stopWatching();
    dialog.classList.add(CORNER_CLASS);
    dialog.removeAttribute('scroll-lock');
    dialog.show();

    window.setTimeout(() => {
      this.addEventListener('keydown', this.#handleCornerKeyDown);
    });
  }

  #closeDialog = async () => {
    if (this.#isCorner || this.#isExitIntent) {
      await this.#closeCornerDialog();
      return;
    }

    await this.#parentCloseDialog();
  };

  #closeCornerDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener('keydown', this.#handleCornerKeyDown);
    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove('dialog-closing');

    if (!this.#isExitIntent) {
      dialog.classList.remove(CORNER_CLASS);
    }

    this.#isCorner = false;
  };

  #bindFormSubmit() {
    this.addEventListener('submit', this.#onFormSubmit);
  }

  /**
   * Keep the visitor on the page. Mailchimp's list-manage endpoints send no CORS
   * headers and their JSONP endpoint now 404s, so this posts in no-cors mode: the
   * request is delivered and a network failure still rejects, but the response is
   * opaque. We can tell Mailchimp received the address, not whether it accepted
   * it -- an already-subscribed address reports the same as a new one.
   *
   * @param {SubmitEvent} event
   */
  #onFormSubmit = (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;
    if (!('mailchimpForm' in form.dataset)) return;

    event.preventDefault();

    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    this.#subscribe(form);
  };

  /**
   * @param {HTMLFormElement} form
   */
  async #subscribe(form) {
    const { sendingText, successText, errorText } = form.dataset;
    const body = new URLSearchParams();

    for (const [key, value] of new FormData(form).entries()) {
      if (typeof value === 'string') body.append(key, value);
    }

    // The classic embed's submit button is part of the payload.
    body.append('subscribe', 'Subscribe');

    this.#setPending(form, true, sendingText);

    try {
      await fetch(form.action, {
        method: 'POST',
        mode: 'no-cors',
        credentials: 'omit',
        body,
      });
    } catch {
      // Rejects only on a network-level failure: offline, DNS, or a blocker
      // stopping list-manage.com. Anything Mailchimp answers resolves opaquely.
      this.#showResponse(form, 'error', errorText ?? '');
      this.#setPending(form, false, sendingText);
      return;
    }

    this.#showResponse(form, 'success', successText ?? '');
    markMailchimpSubscribed();
    form.reset();
    this.#restoreSubmitLabel(form);

    window.setTimeout(() => this.closeDialog(), SUCCESS_CLOSE_DELAY);
  }

  /**
   * @param {HTMLFormElement} form
   * @param {boolean} pending
   * @param {string} [sendingText]
   */
  #setPending(form, pending, sendingText) {
    const button = form.querySelector('[data-mailchimp-submit]');
    const label = form.querySelector('[data-mailchimp-submit-label]');

    if (!(button instanceof HTMLButtonElement)) return;

    if (!pending) {
      this.#restoreSubmitLabel(form);
      button.disabled = false;
      return;
    }

    if (label && sendingText) {
      this.#submitLabel ??= label.textContent ?? '';
      label.textContent = sendingText;
    }

    button.disabled = true;
  }

  /**
   * Put the button's own label back, leaving its disabled state alone: after a
   * successful signup the button stays disabled.
   *
   * @param {HTMLFormElement} form
   */
  #restoreSubmitLabel(form) {
    const label = form.querySelector('[data-mailchimp-submit-label]');

    if (label && this.#submitLabel !== null) {
      label.textContent = this.#submitLabel;
    }
  }

  /**
   * @param {HTMLFormElement} form
   * @param {'success' | 'error'} kind
   * @param {string} message
   */
  #showResponse(form, kind, message) {
    const target = form.querySelector(`[data-mailchimp-${kind}]`);
    const other = form.querySelector(`[data-mailchimp-${kind === 'success' ? 'error' : 'success'}]`);

    other?.setAttribute('hidden', '');

    if (!target) return;

    // Mailchimp's own messages carry markup, e.g. a link for an existing contact.
    target.innerHTML = message;
    target.hidden = false;
  }

  #onSubscribed = () => {
    if (!this.#isExitIntent) return;

    this.#stopWatching();

    if (this.refs.dialog.open) {
      this.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleCornerKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentLeave = (event) => {
    if (this.refs.dialog.open) return;
    if (hasMailchimpSubscribed()) return;
    if (this.#isBlockModalOpen()) return;
    if (!this.#leftTheWindow(event)) return;

    this.showCornerDialog();
  };

  /**
   * @returns {boolean}
   */
  #isBlockModalOpen() {
    const popups = document.querySelectorAll('mailchimp-popup:not([data-exit-intent])');

    for (const popup of popups) {
      const dialog = popup.querySelector('dialog');

      if (dialog?.open) return true;
    }

    return false;
  }

  /**
   * @param {MouseEvent} event
   * @returns {boolean}
   */
  #leftTheWindow(event) {
    return (
      event.clientY <= 0 ||
      event.clientX <= 0 ||
      event.clientX >= window.innerWidth ||
      event.clientY >= window.innerHeight
    );
  }

  #stopWatching() {
    if (this.#intentTimer !== null) {
      window.clearTimeout(this.#intentTimer);
      this.#intentTimer = null;
    }

    document.documentElement.removeEventListener('mouseleave', this.#onDocumentLeave);
    document.removeEventListener('mailchimp:subscribed', this.#onSubscribed);
  }
}

if (!customElements.get('mailchimp-popup')) {
  customElements.define('mailchimp-popup', MailchimpPopup);
}
